<template>
	<div class="kpi-bar-widget">
		<div v-if="!isKpiAvailable" class="widget-unavailable">
			<span>{{ $t('dashboard_widget_unavailable') }}</span>
		</div>
		<div v-else-if="!kpiValue" class="widget-unavailable">
			<span>{{ $t('dashboard_no_data') }}</span>
		</div>
		<BarItem
			v-else
			componentType="info"
			class="kpi-card"
			:label="$t(kpiName)"
			:text="kpiValue"
			:trendInfo="kpiTrendInfo"
			:iconUrl="kpiIconUrl"
			:spaceIconText="false"
		/>
	</div>
</template>

<script>
import BarItem from '../components/BarItem'
import { getOverviewSums } from '../services/performanceService'
import { trendArrowColors } from '../consts/consts'
import utilService from '../../common/services/utilService'
import { mapGetters } from 'vuex'
import { isKpiAvailable as isKpiAvailableForSite, buildWidgetSiteContext } from './widgetRegistry'

const KPI_ICONS = {
	avg_score: 'assets/performance/avg_score-icon.svg',
	complaints: 'assets/performance/icons-complaint.svg',
	visits: 'assets/performance/icons-visits.svg',
	response_time_SLA: 'assets/performance/icons-timer.svg',
	time_saved: 'assets/performance/clock.svg',
	exposures: 'assets/performance/man-exposures.svg'
}

export default {
	name: 'KpiBarWidget',
	props: {
		config: { type: Object, required: true },
		widgetData: { type: Object, required: false, default: () => ({}) }
	},
	computed: {
		...mapGetters(['overviewData', 'trendInfo', 'currentFilteringSite', 'currentSiteInfo']),
		kpiName() {
			return KPI_ICONS[this.config.kpi] ? this.config.kpi : 'avg_score'
		},
		isKpiAvailable() {
			return isKpiAvailableForSite(
				this.kpiName,
				buildWidgetSiteContext({
					currentFilteringSite: this.currentFilteringSite,
					currentSiteInfo: this.currentSiteInfo
				})
			)
		},
		overviewSums() {
			if (!this.overviewData) return null
			return getOverviewSums(this.overviewData)
		},
		kpiValue() {
			if (!this.overviewSums) return null
			const value = this.overviewSums[this.kpiName]
			if (value === null || value === undefined) return null
			// Same formatting rules as the analytics info bar (Analytics.vue refreshData)
			if (this.kpiName === 'avg_score' || this.kpiName === 'response_time_SLA') {
				let text = utilService.kMinutesToHours(value)
				if (this.kpiName === 'response_time_SLA') text += ` ${this.$t('min')}`
				return text
			}
			if (this.kpiName === 'exposures') return `${value}%`
			return utilService.kFormatter(value)
		},
		kpiTrendInfo() {
			const trendInfo = this.trendInfo
			if (!trendInfo || !trendInfo[this.kpiName]) return null
			const info = { ...trendInfo[this.kpiName] }
			if (trendArrowColors[this.kpiName]) {
				info.color = trendArrowColors[this.kpiName][info.upDown]
			}
			return info
		},
		kpiIconUrl() {
			return KPI_ICONS[this.kpiName]
		}
	},
	components: { BarItem }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.kpi-bar-widget {
	height: 100%;
	width: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;

	.kpi-card {
		flex: 1;
		min-width: 0;
		max-width: none;
		min-height: 0;
		max-height: none;
	}

	::v-deep .card.info {
		min-width: 0;
		max-width: none;
		width: 100%;
		height: 100%;
		box-sizing: border-box;
	}
}

.widget-unavailable {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #848593;
	font-size: 0.875rem;
	text-align: center;
	padding: 10px;
	@include border($grey-border-color);
	border-radius: 10px;
	background: #fafbfc;
}
</style>
